import { Music, demoBeat, loadVolume, saveVolume, type MusicState } from './audio/music';
import { backendOption, loadEngine } from './engine/loadEngine';
import { KeyboardControls, MoveMixer } from './input';
import { TouchStick } from './touch';
import { GatewayClient } from './net/gatewayClient';
import {
  DANCES,
  CloseCodes,
  eventFromSearch,
  type EventInfo,
  type ServerMessage,
  type WelcomeMessage,
} from './net/protocol';
import { appendChat } from './ui/chat';
import { GlTimer } from './glTimer';
import { OrbitCamera, attachOrbitControls } from './orbit';
import { compactCount, lodSummary, stressOptions } from './stress';
import { World } from './world';

function $<T extends HTMLElement>(selector: string): T {
  const el = document.querySelector<T>(selector);
  if (!el) throw new Error(`Missing element ${selector}`);
  return el;
}

// The canvas box, its backing store and the surface config have to agree, and on WebGPU the
// browser rounds the backing store on its own: Dawn allocates the canvas image with the
// height rounded up to 256 while wgpu sizes the swapchain import from the height rounded up
// to 64. When the roundings differ (any height that is not a multiple of 256) the import is
// "smaller than the image requires" and every frame is dropped — a black canvas with no
// exception, and WebGL2, which reallocates by itself, shows nothing. So keep the height on a
// 256 boundary and scale the width by the same factor: the image is supersampled, not
// stretched. Cost is fill rate, ~7% over the box.
const SIZE_UNIT = 256;

function canvasSize(canvas: HTMLCanvasElement): {
  w: number;
  h: number;
  dpr: number;
} {
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const box = canvas.getBoundingClientRect();
  const rawW = Math.max(1, Math.round(box.width * dpr));
  const rawH = Math.max(1, Math.round(box.height * dpr));
  const h = Math.max(SIZE_UNIT, Math.ceil(rawH / SIZE_UNIT) * SIZE_UNIT);
  const w = Math.max(1, Math.round(rawW * Math.min(4, h / rawH)));
  return { w, h, dpr };
}

// `let`: if WebGPU can't present, the engine moves to a fresh canvas (see loadEngine).
let canvas = $<HTMLCanvasElement>('#scene');
const hudRegion = $<HTMLSpanElement>('#hud-region');
const hudStats = $<HTMLSpanElement>('#hud-stats');
const musicButton = $<HTMLButtonElement>('#music-button');
const volumeControl = $<HTMLLabelElement>('#volume');
const volumeSlider = $<HTMLInputElement>('#volume-slider');
const volumeIcon = $<HTMLSpanElement>('#volume-icon');
const loginForm = $<HTMLFormElement>('#login');
const loginGrid = $<HTMLParagraphElement>('#login-grid');
const loginEvent = $<HTMLParagraphElement>('#login-event');
const loginError = $<HTMLParagraphElement>('#login-error');
const loginButton = loginForm.querySelector<HTMLButtonElement>('button[type=submit]');
const chatPanel = $<HTMLElement>('#chat');
const chatLog = $<HTMLOListElement>('#chat-log');
const chatForm = $<HTMLFormElement>('#chat-form');
const chatInput = $<HTMLInputElement>('#chat-input');
const chatToggle = $<HTMLButtonElement>('#chat-toggle');
const stickPad = $<HTMLDivElement>('#stick');
const stickKnob = $<HTMLDivElement>('#stick-knob');

// Touch layout (the walking pad, touch hints): for a touch-first screen, and as soon as
// anyone touches the screen of a laptop that has one.
document.body.classList.toggle('touch', window.matchMedia('(pointer: coarse)').matches);
window.addEventListener(
  'pointerdown',
  (e) => {
    if (e.pointerType === 'touch') document.body.classList.add('touch');
  },
  { passive: true },
);
const danceSelect = $<HTMLSelectElement>('#dance-select');
const banner = $<HTMLDivElement>('#banner');

/**
 * The event a shared link leads to, on the login screen: its title and region, or a note that
 * this grid doesn't know it (the visitor can still log in, and arrives where they normally
 * would). Titles come from the gateway's config; textContent keeps them text.
 */
function showEvent(event: EventInfo | undefined, requested: string | null): void {
  loginEvent.classList.remove('unknown');
  if (event) {
    loginEvent.textContent = `🎧 ${event.title} · ${event.region}`;
    document.title = `${event.title} · opensim-web`;
    loginEvent.hidden = false;
  } else if (requested) {
    loginEvent.textContent = `There's no event "${requested}" on this grid; you'll arrive in your usual place.`;
    loginEvent.classList.add('unknown');
    loginEvent.hidden = false;
  } else {
    loginEvent.hidden = true;
  }
}

function showBanner(text: string, withReload = true): void {
  banner.replaceChildren(document.createTextNode(text));
  if (withReload) {
    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = 'Reload';
    button.addEventListener('click', () => location.reload());
    banner.append(button);
  }
  banner.hidden = false;
}

async function main(): Promise<void> {
  // Never let the login form submit natively. The real handler (below) is attached only
  // once the engine has loaded, and until then a click or Enter would POST the password to
  // the web server and replace the app with the response. That really happened on a slow
  // engine load. Attach this guard before the first await so no window is left open.
  loginForm.addEventListener('submit', (e) => e.preventDefault());

  // Size the canvas before the engine looks at it: Engine::create configures the surface
  // from the canvas element's own width/height, and on WebGPU a surface configured at
  // Chrome's default 300×150 can never be reconciled with the image the browser allocates
  // for the real box — every frame then fails on a texture-size mismatch.
  const first = canvasSize(canvas);
  canvas.width = first.w;
  canvas.height = first.h;

  // Start the engine and the gateway connection in parallel.
  const enginePromise = loadEngine(canvas, backendOption(window.location.search));
  const gateway = new GatewayClient();
  // A shared link like ?event=friday-house: the gateway says in its welcome whether it knows it.
  const requestedEvent = eventFromSearch(window.location.search);
  const welcomePromise = gateway.connect(requestedEvent);

  let welcome: WelcomeMessage;
  try {
    welcome = await welcomePromise;
  } catch (err) {
    loginGrid.textContent = 'Could not reach the gateway.';
    showBanner(`Gateway unavailable: ${(err as Error).message}`);
    return;
  }

  loginGrid.textContent =
    welcome.mode === 'demo' ? `${welcome.gridName} · demo mode — any name works` : welcome.gridName;
  showEvent(welcome.event, requestedEvent);
  if (welcome.mode === 'demo') {
    (loginForm.elements.namedItem('firstName') as HTMLInputElement).value ||= 'Guest';
    (loginForm.elements.namedItem('lastName') as HTMLInputElement).value ||= 'Dancer';
  }

  const loaded = await enginePromise.catch((err: unknown) => {
    showBanner(`3D view unavailable: ${(err as Error).message}. Try a browser with WebGPU or WebGL2.`, false);
    return null;
  });
  if (!loaded) return;
  const { engine } = loaded;
  canvas = loaded.canvas;
  console.info(`Engine backend: ${engine.backend}`);
  const stress = stressOptions(window.location.search);
  const showStats = new URLSearchParams(window.location.search).has('stats');
  if (stress) {
    engine.setStressAvatars(stress.triangles, stress.parts, stress.bones, stress.lod, stress.impostorPx);
  }
  // GPU time: the engine measures it on WebGPU; on WebGL2 the viewer times render() with a
  // GL timer query where the browser allows. Only now, after the engine exists: a
  // getContext('webgl2') before wgpu's own would create the context with default attributes.
  const glTimer = engine.backend === 'Gl' ? GlTimer.create(canvas.getContext('webgl2')) : null;
  console.info(
    `GPU timing: ${engine.backend !== 'Gl' ? 'engine timestamp queries (if supported)' : glTimer ? 'WebGL2 timer queries' : 'unavailable'}`,
  );

  const world = new World(engine);
  const music = new Music();
  const isDemo = welcome.mode === 'demo';
  let rttMs: number | null = null;

  // ── canvas size ──
  // The canvas box, its backing store and the surface config have to agree. WebGPU fails
  // every frame on a stale surface — black canvas, no exception — while WebGL2 reallocates
  // on its own and shows nothing at all. Canvas and surface always move together here:
  // one assignment pair, one engine.resize, for every path that changes the size.
  let requested: { w: number; h: number } = first;
  let applied: { w: number; h: number } = first;
  const apply = (w: number, h: number, dpr: number): void => {
    canvas.width = w;
    canvas.height = h;
    engine.resize(w, h);
    // The backing store is snapped to sizes WebGPU can present, so measure the real ratio.
    engine.setPixelRatio(h / Math.max(1, canvas.clientHeight));
    applied = { w, h };
    console.info(
      `surface ${w}×${h} (canvas box ${Math.round(w / dpr)}×${Math.round(h / dpr)} css at dpr ${dpr})`,
    );
  };
  const resize = (): void => {
    const { w, h, dpr } = canvasSize(canvas);
    if (requested.w === w && requested.h === h) {
      // Same box: heal drift nobody reported. A dpr change resets the backing store and
      // fires no resize event, so restore the size the surface is configured at.
      if (canvas.width !== applied.w || canvas.height !== applied.h) {
        canvas.width = applied.w;
        canvas.height = applied.h;
      }
      return;
    }
    requested = { w, h };
    apply(w, h, dpr);
  };
  new ResizeObserver(resize).observe(canvas);
  window.addEventListener('resize', resize);
  // A dpr change fires no resize event at all, and it changes the size the compositor
  // presents at: fractional Linux scaling, zoom, a monitor move, or dpr settling after
  // load. Re-arm the query each time, since its value is baked into the media query.
  const watchDpr = (): void =>
    window.matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`).addEventListener(
      'change',
      () => {
        resize();
        watchDpr();
      },
      { once: true },
    );
  watchDpr();
  resize();

  // ── messages ──
  gateway.onMessage((message: ServerMessage) => {
    if (world.apply(message)) {
      if (message.type === 'region') hudRegion.textContent = message.name;
      // Show our dance as the grid reports it, not just what we picked.
      if (message.type === 'avatarDance' || message.type === 'avatarAdded') {
        danceSelect.value = world.selfDance ?? '';
      }
      return;
    }
    switch (message.type) {
      case 'loginResult':
        if (message.ok) {
          loginForm.hidden = true;
          chatPanel.hidden = false;
          stickPad.hidden = false; // shown only on touch screens (body.touch)
          // Deliberately not focusing the chat box: KeyboardControls ignores keys while an
          // input has focus, and a visitor's first instinct on arriving is W to walk. Enter
          // focuses the chat box, and blur returns the keys to movement.
        } else {
          loginError.textContent = message.message ?? 'Login failed.';
          if (loginButton) loginButton.disabled = false;
        }
        break;
      case 'chat':
        appendChat(chatLog, message, message.fromId === world.selfId);
        break;
      case 'music':
        music.setUrl(message.url);
        volumeControl.hidden = !message.url;
        break;
      case 'pong':
        rttMs = performance.now() - message.t;
        break;
      case 'error':
        console.warn(`Gateway error ${message.code}: ${message.message}`);
        break;
      case 'welcome':
        break;
    }
  });

  gateway.onClose((code) => {
    const why =
      code === CloseCodes.VersionMismatch
        ? 'The viewer is out of date.'
        : code === CloseCodes.GridDisconnected
          ? 'Disconnected from the grid.'
          : `Connection closed (${code}).`;
    showBanner(why);
  });

  // ── login ──
  loginForm.addEventListener('submit', (e) => {
    e.preventDefault();
    const data = new FormData(loginForm);
    loginError.textContent = '';
    if (loginButton) loginButton.disabled = true;
    gateway.send({
      type: 'login',
      firstName: String(data.get('firstName') ?? '').trim(),
      lastName: String(data.get('lastName') ?? '').trim(),
      password: String(data.get('password') ?? ''),
      ...(welcome.event ? { event: welcome.event.id } : {}),
    });
    // The submit click is a user gesture: a good moment to unlock audio.
    void music.unlock();
  });

  // Only now can a login be handled: the gateway is up, the engine is drawing and the
  // handler above is attached.
  if (loginButton) loginButton.disabled = false;

  // ── chat ──
  chatForm.addEventListener('submit', (e) => {
    e.preventDefault();
    const text = chatInput.value.trim();
    if (text) gateway.send({ type: 'chat', text });
    chatInput.value = '';
    // Hand the keys back to movement: KeyboardControls ignores keys while an input has
    // focus, so staying in the box after sending would leave W typing "w" instead of
    // walking. Enter reopens chat; an empty Enter just closes it.
    chatInput.blur();
  });
  window.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && document.activeElement !== chatInput && !chatPanel.hidden) {
      chatInput.focus();
      e.preventDefault();
    } else if (e.key === 'Escape' && document.activeElement === chatInput) {
      chatInput.blur();
    }
  });

  // ── music ──
  // Volume: remembered per browser, applied after the beat analysis (see Music.volume).
  const showVolume = (v: number): void => {
    volumeIcon.textContent = v === 0 ? '🔇' : '🔊';
    volumeSlider.value = String(v);
  };
  music.volume = loadVolume(localStorage);
  showVolume(music.volume);
  volumeSlider.addEventListener('input', () => {
    music.volume = Number(volumeSlider.value);
    showVolume(music.volume);
    saveVolume(localStorage, music.volume);
  });

  // The play button follows the music's state: offered whenever there's music the browser
  // won't start without a click (autoplay rules, an interrupted page), and a quiet notice
  // while a dropped stream reconnects by itself.
  const showMusicState = (state: MusicState): void => {
    musicButton.hidden = state !== 'needs-click' && state !== 'reconnecting';
    musicButton.disabled = state === 'reconnecting';
    musicButton.textContent = state === 'reconnecting' ? 'Music reconnecting…' : '▶ Play music';
  };
  music.onStateChange(showMusicState);
  showMusicState(music.state);
  musicButton.addEventListener('click', () => void music.unlock());

  // ── dance ──
  for (const [i, id] of DANCES.entries()) {
    danceSelect.add(new Option(`Dance ${i + 1}`, id));
  }
  danceSelect.addEventListener('change', () => {
    const picked = DANCES.find((d) => d === danceSelect.value) ?? null;
    gateway.send({ type: 'dance', dance: picked });
    // A focused <select> keeps every key (see swallowsKey), so hand them back to walking.
    danceSelect.blur();
  });

  // ── movement + camera ──
  // Remember whether we're moving, so the camera can swing back behind us while walking.
  // Keyboard and the touch pad both feed one control state (MoveMixer), sent on change.
  let walking = false;
  const mixer = new MoveMixer((move) => {
    walking = move.forward !== 0 || move.turn !== 0;
    gateway.send(move);
  });
  new KeyboardControls((move) => mixer.set('keys', move));
  new TouchStick(stickPad, stickKnob, (controls) => mixer.set('touch', controls));

  // Chat can fold away to its input line: on a phone the log covers a lot of club.
  const setChatOpen = (open: boolean): void => {
    chatPanel.classList.toggle('collapsed', !open);
    chatToggle.setAttribute('aria-expanded', String(open));
    chatToggle.textContent = open ? '▾' : '💬';
    chatToggle.title = open ? 'Hide chat' : 'Show chat';
  };
  chatToggle.addEventListener('click', () => setChatOpen(chatPanel.classList.contains('collapsed')));
  const orbit = new OrbitCamera();
  attachOrbitControls(canvas, orbit);

  // ── ping ──
  setInterval(() => gateway.send({ type: 'ping', t: performance.now() }), 5000);

  // ── frame loop ──
  let lastEnergy = 0;
  /** Dev only: an energy the browser check holds the lights at, instead of the music's. */
  let heldEnergy: number | null = null;
  if (import.meta.env.DEV) {
    // For the browser checks (scripts/browser-check.mjs): Vite drops this from builds.
    window.__viewerDebug = {
      energy: () => lastEnergy,
      analysing: () => music.analysing,
      musicState: () => music.state,
      holdEnergy: (energy: number | null) => {
        heldEnergy = energy;
      },
    };
  }
  let frames = 0;
  let lastStats = performance.now();
  let lastFrame = performance.now();
  const frame = (t: number): void => {
    const energy = heldEnergy ?? (music.analysing ? music.energy(t) : isDemo ? demoBeat(t) * 0.8 : 0);
    lastEnergy = energy;
    engine.setAudioEnergy(energy);
    orbit.update((t - lastFrame) / 1000, walking);
    lastFrame = t;
    engine.setCameraOrbit(orbit.yaw, orbit.pitch, orbit.distance);
    glTimer?.begin();
    engine.render(t);
    glTimer?.end();

    frames++;
    if (t - lastStats > 500) {
      resize();
      const fps = (frames * 1000) / (t - lastStats);
      const backend = engine.backend === 'Gl' ? 'WebGL2' : 'WebGPU';
      // "rtt": round trip to the gateway, not frame time. GPU time is labelled separately.
      const rtt = rttMs === null ? '' : ` · rtt ${Math.round(rttMs)} ms`;
      const gpuMs = Number.isFinite(engine.gpuTimeMs) ? engine.gpuTimeMs : glTimer?.lastMs;
      const gpu = gpuMs == null ? '' : ` · GPU ${gpuMs.toFixed(1)} ms`;
      // In load-test mode, show the load too: it's what the fps is measured against.
      // Load test (?avatar=stress) or ?stats=1: what the frame costs, for measuring a big build.
      const load =
        stress || showStats
          ? ` · ${compactCount(engine.triangles)} tris · ${engine.drawCalls} draws` +
            (stress ? ` · ${lodSummary(engine.lodCounts)}` : '')
          : '';
      hudStats.textContent = `${fps.toFixed(0)} fps${gpu} · ${backend} · ${world.avatarCount} avatars · ${world.primCount} prims${load}${rtt}`;
      frames = 0;
      lastStats = t;
    }
    requestAnimationFrame(frame);
  };
  requestAnimationFrame(frame);

  window.addEventListener('pagehide', () => gateway.close());
}

void main();
